import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { api } from '../services/api';
import {
  normalizeCriteriaSections,
  getEligibilityFromCriteriaResult,
  unwrapCriteriaPayload,
} from '../utils/criteriaResult';

function JobDetail() {
  const { jobId } = useParams();
  const { logout } = useAuth();
  const { t } = useLanguage();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (jobId) {
      loadJob();
      // Poll for updates if job is still processing
      const interval = setInterval(() => {
        if (job?.status === 'processing' || job?.status === 'pending') {
          loadJob();
        }
      }, 3000);
      return () => clearInterval(interval);
    }
    // loadJob is stable per jobId; interval handles polling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, job?.status]);

  const loadJob = async () => {
    if (!jobId) return;

    try {
      const response = await api.get(`/jobs/${jobId}`);
      setJob(response.data);
      setError('');
    } catch (err) {
      console.error('Failed to load job:', err);
      setError(err.response?.data?.error || err.message || t('jobDetail.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'done':
        return '#10b981';
      case 'processing':
        return '#3b82f6';
      case 'failed':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const criteriaSections = job?.result ? normalizeCriteriaSections(job.result) : null;
  const { resultNode } = job?.result ? unwrapCriteriaPayload(job.result) : { resultNode: null };
  const hasCriteriaContent =
    (Array.isArray(criteriaSections) && criteriaSections.length > 0) ||
    (resultNode != null && String(resultNode).trim() !== '');
  const eligibility = job?.result && hasCriteriaContent ? getEligibilityFromCriteriaResult(job.result) : null;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', color: '#666', marginBottom: '1rem' }}>{t('jobDetail.loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', color: '#ef4444', marginBottom: '1rem' }}>
            {error || t('jobDetail.notFound')}
          </div>
          <Link to="/dashboard" style={{ color: '#667eea', textDecoration: 'none' }}>
            {t('jobDetail.backToDashboard')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      <header style={{
        background: 'white',
        padding: '1rem 2rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{ margin: 0, color: '#333' }}>CreditCharges</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <LanguageSwitch />
          <Link
            to="/dashboard"
            style={{
              padding: '0.5rem 1rem',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
              textDecoration: 'none'
            }}
          >
            Dashboard
          </Link>
          <button
            onClick={logout}
            style={{
              padding: '0.5rem 1rem',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            {t('common.logout')}
          </button>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, color: '#333' }}>{t('jobDetail.title')}</h2>
            <div style={{
              padding: '0.5rem 1rem',
              borderRadius: '12px',
              background: getStatusColor(job.status) + '20',
              color: getStatusColor(job.status),
              fontWeight: '500',
              textTransform: 'capitalize'
            }}>
              {job.status}
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '4px' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: '#666' }}>{t('jobDetail.jobId')}</strong>{' '}
              <span style={{ color: '#333', fontFamily: 'monospace' }}>{job.jobId}</span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: '#666' }}>Created:</strong>{' '}
              <span style={{ color: '#333' }}>{new Date(job.createdAt).toLocaleString()}</span>
            </div>
            {job.completedAt && (
              <div>
                <strong style={{ color: '#666' }}>Completed:</strong>{' '}
                <span style={{ color: '#333' }}>{new Date(job.completedAt).toLocaleString()}</span>
              </div>
            )}
          </div>

          {job.status === 'processing' || job.status === 'pending' ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              background: '#eff6ff',
              borderRadius: '4px',
              color: '#3b82f6'
            }}>
              <div style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>
                Analysis in progress...
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                This page will update automatically when the analysis is complete.
              </div>
            </div>
          ) : job.status === 'failed' ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              background: '#fef2f2',
              borderRadius: '4px',
              color: '#ef4444'
            }}>
              <div style={{ fontSize: '1.25rem' }}>
                Analysis failed
              </div>
              <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Please try uploading the contract again.
              </div>
            </div>
          ) : job.status === 'done' ? (
            <div>
              <h3 style={{ color: '#333', marginBottom: '1rem' }}>Analysis Results</h3>

              {/* Claim outcome from criteria (aligned with Loan_Fees_Analysis PDF: Territorial, Temporal, Reimbursement threshold, Borrower) */}
              {eligibility && (
                <div style={{
                  padding: '1.5rem',
                  borderRadius: '4px',
                  marginBottom: '1.5rem',
                  border: '1px solid',
                  ...(eligibility.eligible === false
                    ? { background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }
                    : eligibility.eligible === null || eligibility.manualReviewRequired
                      ? { background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }
                      : { background: '#f0fdf4', borderColor: '#86efac', color: '#166534' })
                }}>
                  <h4 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1rem' }}>
                    {t('jobDetail.claimOutcome')}
                  </h4>
                  {eligibility.eligible === false ? (
                    <>
                      <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600' }}>{t('jobDetail.noClaim')}</p>
                      {eligibility.notEligibleReasons.length > 0 && (
                        <>
                          <p style={{ margin: '0.5rem 0 0.25rem 0', fontSize: '0.875rem' }}>{t('jobDetail.reason')}</p>
                          <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                            {eligibility.notEligibleReasons.map((reason, i) => (
                              <li key={i}>{reason}</li>
                            ))}
                          </ul>
                        </>
                      )}
                    </>
                  ) : eligibility.eligible === null || eligibility.manualReviewRequired ? (
                    <p style={{ margin: 0, fontWeight: '500' }}>{t('jobDetail.manualReview')}</p>
                  ) : (
                    <p style={{ margin: 0, fontWeight: '600' }}>{t('jobDetail.possibleClaim')}</p>
                  )}
                </div>
              )}

              {job.resultHuman && (
                <div style={{
                  padding: '1.5rem',
                  background: '#f0fdf4',
                  borderRadius: '4px',
                  marginBottom: '1.5rem',
                  border: '1px solid #86efac'
                }}>
                  <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#166534' }}>Summary</h4>
                  <div style={{ color: '#166534', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                    {job.resultHuman}
                  </div>
                </div>
              )}

              {job.result && (
                <div style={{
                  padding: '1.5rem',
                  background: '#f9fafb',
                  borderRadius: '4px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>Detailed Results</h4>
                  <pre style={{
                    margin: 0,
                    padding: '1rem',
                    background: 'white',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    color: '#333'
                  }}>
                    {JSON.stringify(job.result, null, 2)}
                  </pre>
                </div>
              )}

              {!job.result && !job.resultHuman && (
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  No results available for this job.
                </div>
              )}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}

export default JobDetail;

